import axios from 'axios';
import * as Sentry from '@sentry/react';
import { bablerBaseUrl, GET } from '../../../../utils/axios-interceptor';
import { getGlobalAuthHeaders, getStoredValue } from '../../service';
import { BABLER_SUCCESS_CODE, HTTP_NOT_FOUND, WHATSAPP_ENDPOINTS, WHATSAPP_ITEM_KIND, WHATSAPP_TIMELINE_PAGE_SIZE } from '../constants';
import {
    BablerTimelineRaw,
    BablerWhatsAppResponse,
    WhatsAppChatSource,
    WhatsAppChatSummary,
    WhatsAppItemKind,
    WhatsAppTimelineItem,
    WhatsAppTimelinePage,
} from '../types';

/**
 * Dedicated axios instance with NO interceptors, on purpose: the app-wide instance logs the
 * user out on any 401 (utils/axios-interceptor.ts), and babler turns even a Cerberus timeout
 * into a 401. A failed WhatsApp call must never end the session.
 * `axios.create()` merges the caller's current defaults, so the stale `x-organization-id`
 * default stamped on the global instance is deleted (same as globalAskAica/service.ts).
 */
export const bablerWhatsAppAxios = axios.create();
delete bablerWhatsAppAxios.defaults.headers['x-organization-id'];

// Cerberus v2 takes the PNO from the token and ignores this org header; sent only to match the other babler calls.
const getAuthHeaders = (): Record<string, string> => getGlobalAuthHeaders(getStoredValue('org_id'));

const isSuccess = (data?: BablerWhatsAppResponse): boolean => data?.responseData?.responseCode === BABLER_SUCCESS_CODE;

const KNOWN_KINDS: readonly string[] = Object.values(WHATSAPP_ITEM_KIND);

/** Reports a failed list refresh. Carries only status/code/message, never headers or the token. */
const reportListFailure = (reason: string, details: { status?: number; responseCode?: number; message?: string }) =>
    Sentry.withScope((scope) => {
        scope.setTag('whatsapp_chat_on_web', 'list-failed');
        scope.setFingerprint(['whatsapp-chat-on-web', 'list-failed', reason]);
        scope.setLevel('warning');
        scope.setContext('whatsapp_list_failure', { reason, ...details });
        Sentry.captureMessage(`whatsapp-chat-on-web: list failed (${reason})`);
    });

/** Returns the item when it has the fields the view relies on and a known kind; otherwise null. */
export const toTimelineItem = (raw: BablerTimelineRaw): WhatsAppTimelineItem | null => {
    if (!raw?.id || !raw.at || !raw.kind || !KNOWN_KINDS.includes(raw.kind)) return null;
    return {
        id: raw.id,
        kind: raw.kind as WhatsAppItemKind,
        at: raw.at,
        text: raw.text ?? undefined,
        file: raw.file ?? undefined,
        list: raw.list ?? undefined,
    };
};

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value !== '';

/** A chat the sidebar can render: id, title and last-message time are all non-empty strings. */
const isValidChat = (c: WhatsAppChatSummary): boolean =>
    isNonEmptyString(c?.chatId) && isNonEmptyString(c.title) && isNonEmptyString(c.lastMessageAt);

export const bablerWhatsAppSource: WhatsAppChatSource = {
    async listChats(): Promise<WhatsAppChatSummary[] | null> {
        try {
            const { data } = await bablerWhatsAppAxios<BablerWhatsAppResponse>({
                method: GET,
                url: `${bablerBaseUrl}${WHATSAPP_ENDPOINTS.CHATS}`,
                headers: getAuthHeaders(),
            });
            if (!isSuccess(data) || !Array.isArray(data.waChats)) {
                reportListFailure(isSuccess(data) ? 'malformed-body' : 'non-success-code', {
                    responseCode: data?.responseData?.responseCode,
                    message: data?.responseData?.responseMessage,
                });
                return null;
            }
            return data.waChats.filter(isValidChat).map((c) => ({ chatId: c.chatId, title: c.title, lastMessageAt: c.lastMessageAt }));
        } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            // Old babler without the endpoint: quietly no WhatsApp chats.
            if (status === HTTP_NOT_FOUND) return [];
            reportListFailure('request-failed', { status });
            return null;
        }
    },

    async getTimeline(chatId: string, before?: number): Promise<WhatsAppTimelinePage> {
        const { data } = await bablerWhatsAppAxios<BablerWhatsAppResponse>({
            method: GET,
            url: `${bablerBaseUrl}${WHATSAPP_ENDPOINTS.timeline(chatId)}`,
            headers: getAuthHeaders(),
            params: { limit: WHATSAPP_TIMELINE_PAGE_SIZE, ...(before !== undefined ? { before } : {}) },
        });
        // "Chat not found" / "Invalid request" / "Could not verify the caller" (code 40) arrive as HTTP 200.
        if (!isSuccess(data) || !Array.isArray(data.waTimeline))
            throw new Error(data?.responseData?.responseMessage ?? 'WhatsApp timeline failed');
        const items = data.waTimeline.map(toTimelineItem).filter((item): item is WhatsAppTimelineItem => item !== null);
        return { items, nextBefore: data.nextBefore ?? null };
    },
};
